/**
 * Company and startup hiring: requirements, matched talent, the hiring pipeline and freelance applications.
 * Candidate contact details only appear after the organisation shortlists someone (the server enforces this).
 */
import { api } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html, safeUrl } from '../../core/html.js';
import { fmtDate, fmtDateTime, fromIstInput, inr, toPaise } from '../../core/format.js';
import { navigate } from '../../core/router.js';
import { reload, update } from '../../app/shell.js';
import { backLink, banner, bar, chips, empty, ic, pageHead, seg, statusTag, table, tag, who } from '../../ui/components.js';
import { field } from '../../ui/forms.js';
import { closeModal, confirmBox, openModal } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';
import { requirementBody, requirementFields } from '../requirements/form.js';

const ROLES = ['company', 'startup'];
const FREELANCE = new Set(['Freelance', 'Internship']);
const STAGES = ['Shortlisted', 'Interview', 'Offer', 'Hired', 'Rejected'];
const quiet = (p, fallback) => p.catch(() => fallback);
const forRoles = (make) => ROLES.map(make);

/* ---------- requirement list ---------- */

function listRoute(role) {
  return {
    path: `/${role}/requirements`,
    roles: [role],
    title: 'Requirements',
    load: (ctx) => api.get('/org/requirements', { signal: ctx.signal }),
    view(d) {
      return html`${pageHead('Requirements', 'Tell Vignova who you need. We review each requirement, then match verified candidates.', html`<a class="btn" href="/${role}/requirements/new">${ic('plus')} Post requirement</a>`)}
        ${d.requirements.length
          ? html`<div class="grid g2">${d.requirements.map((r) => html`<div class="card"><div class="row between">${tag(r.type)}${statusTag(r.status)}</div>
            <h3 class="mt-s"><a href="/${role}/requirements/${r.id}">${r.title}</a></h3>
            <p class="sm mu">${r.count} ${r.count === 1 ? 'person' : 'people'}${r.location ? `, ${r.location}` : ''}${r.work_mode ? `, ${r.work_mode}` : ''}. Posted ${fmtDate(r.created_at)}. Ref ${r.ref}.</p>
            ${chips(r.skills)}
            <div class="row between mt-s"><span class="sm">${r.status === 'in_review' ? 'Vignova is reviewing this' : html`<b>${r.matched}</b> matched ${r.matched === 1 ? 'profile' : 'profiles'}`}</span>
              <a class="btn ghost sm" href="/${role}/requirements/${r.id}">Open</a></div></div>`)}</div>`
          : empty('clip', 'No requirements yet', 'Post what you need and Vignova starts matching.', html`<a class="btn" href="/${role}/requirements/new">Post requirement</a>`)}`;
    },
  };
}

function newRoute(role) {
  return {
    path: `/${role}/requirements/new`,
    roles: [role],
    nav: 'requirements',
    title: 'Post a requirement',
    ui: () => ({ error: '' }),
    view(_, ui) {
      return html`${backLink(`/${role}/requirements`, 'Requirements')}
        <div class="card" style="max-width:760px"><h2>Post a requirement</h2>
          <form data-submit="req.create" data-role="${role}" novalidate>${requirementFields({ prefix: 'nr' })}
            ${ui.error ? banner(ui.error, 'bad', 'alert') : ''}<button class="btn lg mt" type="submit">Submit for review</button></form>
          <p class="note mt-s">${ic('shield')} Vignova checks each requirement before candidates are matched. This usually takes one working day.</p></div>`;
    },
  };
}

register({
  'req.create': (form) => submitForm(form, async (values) => {
    const { requirement } = await api.post('/org/requirements', { body: requirementBody(values) });
    toast('Requirement submitted. Vignova will review it.', 'send');
    await navigate(`/${form.dataset.role}/requirements/${requirement.id}`);
  }, { onError: (error) => toast(error.message, 'alert') }),
});

/* ---------- requirement hub ---------- */

function tabsFor(r, d) {
  const tabs = [['talent', `Matched talent (${d.talent.length})`], ['pipeline', `Pipeline (${d.pipeline.length})`]];
  if (FREELANCE.has(r.type)) tabs.push(['applications', `Applications (${d.applications.length})`]);
  return tabs;
}

function detailRoute(role) {
  return {
    path: `/${role}/requirements/:id`,
    roles: [role],
    nav: 'requirements',
    title: (d) => (d ? d.requirement.title : 'Requirement'),
    ui: (ctx) => ({ tab: ctx.query.get('tab') || 'talent' }),
    async load(ctx) {
      const id = Number(ctx.params.id);
      const [talent, pipeline] = await Promise.all([
        api.get(`/org/requirements/${id}/talent`, { signal: ctx.signal }),
        api.get(`/org/requirements/${id}/pipeline`, { signal: ctx.signal }),
      ]);
      const r = talent.requirement;
      const applications = FREELANCE.has(r.type) ? (await quiet(api.get(`/org/requirements/${id}/applications`, { signal: ctx.signal }), { applications: [] })).applications : [];
      return { requirement: r, talent: talent.talent, pipeline: pipeline.pipeline, applications };
    },
    view(d, ui) {
      const r = d.requirement;
      const tabs = tabsFor(r, d);
      const tab = tabs.some(([k]) => k === ui.tab) ? ui.tab : 'talent';
      return html`${backLink(`/${role}/requirements`, 'Requirements')}
        <div class="page-h"><div><div class="row">${tag(r.type)}${statusTag(r.status)}</div><h2 style="margin:6px 0 4px">${r.title}</h2>
          <p>${r.count} ${r.count === 1 ? 'person' : 'people'}${r.location ? `, ${r.location}` : ''}${r.work_mode ? `, ${r.work_mode}` : ''}${r.budget_text ? `, ${r.budget_text}` : ''}. Ref ${r.ref}.</p></div>
          ${r.status !== 'closed' ? html`<button type="button" class="btn ghost" data-act="req.close" data-id="${r.id}" data-title="${r.title}">Close requirement</button>` : ''}</div>
        ${chips(r.skills)}
        ${r.status === 'in_review' ? banner('Vignova is reviewing this requirement. Matched candidates appear here once it is approved.', 'wn', 'info') : ''}
        <div class="mt mb">${seg(tabs, tab, 'hub.tab', 'Requirement sections')}</div>
        ${tab === 'talent' ? talentList(d) : tab === 'pipeline' ? pipelineList(d.pipeline, role) : applicationList(d.applications, r, role)}`;
    },
  };
}

function talentCard(t, reqId) {
  return html`<div class="card" id="tal-${t.student_id}"><div class="row between">${who(t.name, [t.degree, t.college].filter(Boolean).join(', ') || t.city)}<div style="text-align:right"><b>${t.score}%</b><div class="xs mu">match</div></div></div>
    ${bar(t.score, t.score >= 70 ? 'ok' : '', `${t.name} match`)}
    ${chips(t.skills)}
    <div class="row sm mt-s">${t.assessment_avg != null ? html`<span>${ic('chart')} Assessments ${t.assessment_avg}%</span>` : ''}<span>${ic('award')} ${t.certificates} ${t.certificates === 1 ? 'certificate' : 'certificates'}</span>${t.city ? html`<span>${t.city}</span>` : ''}</div>
    ${t.projects.length ? html`<div class="chips mt-s">${t.projects.map((p) => tag(p.title, p.verified ? 'ok' : 'n'))}</div>` : ''}
    ${t.shortlisted
      ? html`<div class="mt-s">${tag('In your pipeline', 'ok')} <span class="sm mu">${[t.email, t.phone].filter(Boolean).join(', ')}</span></div>`
      : t.passed ? html`<div class="mt-s">${tag('Passed', 'n')}</div>`
        : html`<div class="row mt-s"><button type="button" class="btn sm" data-act="talent.shortlist" data-req="${reqId}" data-id="${t.student_id}" data-name="${t.name}">${ic('check')} Shortlist</button>
          <button type="button" class="btn ghost sm" data-act="talent.pass" data-req="${reqId}" data-id="${t.student_id}" data-name="${t.name}">Pass</button></div>`}
    <p class="sm mt-s row">${t.resume_url ? html`<a href="${safeUrl(t.resume_url)}#toolbar=0&navpanes=0" target="_blank" rel="noopener noreferrer">${ic('file')} View resume</a>` : ''}
      ${t.portfolio_url ? html`<a href="${safeUrl(t.portfolio_url)}" target="_blank" rel="noopener noreferrer">${ic('link')} Portfolio</a>` : ''}</p></div>`;
}

function talentList(d) {
  if (d.requirement.status === 'in_review') return empty('target', 'Matching has not started', 'We match candidates after reviewing your requirement.');
  if (!d.talent.length) return empty('target', 'No matched profiles yet', 'Vignova is shortlisting verified candidates for this requirement. You will be notified.');
  return html`<div class="grid g2">${d.talent.map((t) => talentCard(t, d.requirement.id))}</div>
    <p class="note mt-s">${ic('lock')} Contact details are shared only after you shortlist a candidate.</p>`;
}

function stageActions(e) {
  if (e.stage === 'Hired' || e.stage === 'Rejected') return '';
  const reject = html`<button type="button" class="btn danger sm" data-act="pipe.reject" data-id="${e.id}" data-name="${e.candidate.name}">Reject</button>`;
  if (e.stage === 'Shortlisted') return html`<div class="row"><button type="button" class="btn sm" data-act="pipe.interview" data-id="${e.id}" data-name="${e.candidate.name}">Schedule interview</button>${reject}</div>`;
  if (e.stage === 'Interview') return html`<div class="row"><button type="button" class="btn sm" data-act="pipe.move" data-id="${e.id}" data-stage="Offer" data-name="${e.candidate.name}">Make offer</button>
    <button type="button" class="btn ghost sm" data-act="pipe.interview" data-id="${e.id}" data-name="${e.candidate.name}">Reschedule</button>${reject}</div>`;
  return html`<div class="row"><button type="button" class="btn sm" data-act="pipe.move" data-id="${e.id}" data-stage="Hired" data-name="${e.candidate.name}">Mark hired</button>${reject}</div>`;
}

export function pipelineList(entries, role, withRequirement = false) {
  if (!entries.length) return empty('users', 'Nobody in the pipeline', 'Shortlist matched candidates to start talking to them.');
  const rows = [...entries].sort((a, b) => STAGES.indexOf(a.stage) - STAGES.indexOf(b.stage));
  return table([...(withRequirement ? ['Requirement'] : []), 'Candidate', 'Stage', 'Interview', 'Contact', ''], rows.map((e) => [
    ...(withRequirement ? [e.requirement_title] : []),
    who(e.candidate.name, e.candidate.college),
    statusTag(e.stage),
    e.interview_at ? html`${fmtDateTime(e.interview_at)}<div class="xs mu">${e.interview_mode}</div>` : html`<span class="mu">Not scheduled</span>`,
    html`<div class="xs">${e.candidate.email || ''}<br>${e.candidate.phone || ''}</div>${e.conversation_id ? html`<a class="btn ghost sm mt-s" href="/${role}/messages?c=${e.conversation_id}">${ic('chat')} Message</a>` : ''}`,
    stageActions(e),
  ]));
}

function applicationList(apps, r, role) {
  if (!apps.length) return empty('send', 'No applications yet', 'Verified freelancers can apply while the requirement is open.');
  return html`<div class="list">${apps.map((a) => html`<div class="card mb"><div class="row between">${who(a.freelancer_name, `Applied ${fmtDate(a.created_at)}`)}<div style="text-align:right"><b>${inr(a.quote_paise)}</b><div>${statusTag(a.status)}</div></div></div>
    <p class="sm mt-s" style="white-space:pre-line">${a.proposal}</p>
    ${a.resume_url ? html`<p class="sm"><a href="${safeUrl(a.resume_url)}#toolbar=0&navpanes=0" target="_blank" rel="noopener noreferrer">${ic('file')} View resume</a></p>` : ''}
    <div class="row">${a.status === 'applied' ? html`<button type="button" class="btn sm" data-act="app.decide" data-id="${a.id}" data-action="shortlist" data-name="${a.freelancer_name}">Shortlist</button>
      <button type="button" class="btn ghost sm" data-act="app.decide" data-id="${a.id}" data-action="reject" data-name="${a.freelancer_name}">Reject</button>` : ''}
      ${a.status === 'shortlisted' ? html`${a.conversation_id ? html`<a class="btn ghost sm" href="/${role}/messages?c=${a.conversation_id}">${ic('chat')} Chat</a>` : ''}
        <button type="button" class="btn sm" data-act="app.hire" data-role="${role}" data-id="${a.id}" data-name="${a.freelancer_name}" data-quote="${a.quote_paise}" data-title="${r.title}">Hire and start project</button>` : ''}</div></div>`)}</div>`;
}

const parseMilestones = (text) => text.split('\n').map((line) => line.trim()).filter(Boolean).map((line) => {
  const [title, amount] = line.split('|').map((x) => x.trim());
  return { title, amount_paise: toPaise(amount ?? '') };
});

register({
  'hub.tab': (el) => update((ui) => { ui.tab = el.dataset.value; }),

  'req.close': async (el) => {
    if (!(await confirmBox({ title: `Close ${el.dataset.title}?`, text: 'No more candidates are matched. Your pipeline stays available.', label: 'Close requirement', danger: true }))) return;
    await api.post(`/org/requirements/${el.dataset.id}/close`);
    toast('Requirement closed', 'out');
    await reload();
  },

  'talent.shortlist': async (el) => {
    if (!(await confirmBox({ title: `Shortlist ${el.dataset.name}?`, text: 'This opens a chat with the candidate and shares your contact details with them. Their contact details are shared with you.', label: 'Shortlist' }))) return;
    await api.post(`/org/requirements/${el.dataset.req}/shortlist`, { body: { student_id: Number(el.dataset.id) } });
    toast(`${el.dataset.name} shortlisted. A chat is open.`, 'check');
    await reload();
  },
  'talent.pass': async (el) => {
    if (!(await confirmBox({ title: `Pass on ${el.dataset.name}?`, text: 'They will not be shown again for this requirement.', label: 'Pass', danger: true }))) return;
    await api.post(`/org/requirements/${el.dataset.req}/pass`, { body: { student_id: Number(el.dataset.id) } });
    await reload();
  },

  'pipe.move': async (el) => {
    await api.post(`/org/pipeline/${el.dataset.id}/stage`, { body: { stage: el.dataset.stage } });
    toast(`${el.dataset.name} moved to ${el.dataset.stage}`, 'check');
    await reload();
  },
  'pipe.reject': async (el) => {
    if (!(await confirmBox({ title: `Reject ${el.dataset.name}?`, text: 'They are told their application did not move forward.', label: 'Reject', danger: true }))) return;
    await api.post(`/org/pipeline/${el.dataset.id}/stage`, { body: { stage: 'Rejected' } });
    await reload();
  },
  'pipe.interview': (el) => openModal({
    title: `Interview: ${el.dataset.name}`,
    body: html`<form id="iv-form" data-submit="pipe.interview.send" data-id="${el.dataset.id}" novalidate>
      ${field('iv-at', 'Date and time (India time)', { name: 'at', type: 'datetime-local' })}
      ${field('iv-mode', 'How', { name: 'mode', minlen: 2, maxlength: 60, ph: 'Google Meet, link shared in chat' })}</form><p class="note">The candidate is notified in their chat.</p>`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="iv-form" class="btn">Schedule</button>`,
  }),
  'pipe.interview.send': (form) => submitForm(form, async ({ at, mode }) => {
    const iso = fromIstInput(at);
    if (!iso || new Date(iso) <= new Date()) throw new Error('Pick a time in the future.');
    await api.post(`/org/pipeline/${form.dataset.id}/interview`, { body: { at: iso, mode } });
    closeModal(false);
    toast('Interview scheduled', 'cal');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),

  'app.decide': async (el) => {
    const shortlist = el.dataset.action === 'shortlist';
    if (!shortlist && !(await confirmBox({ title: `Reject ${el.dataset.name}?`, text: 'They will be told.', label: 'Reject', danger: true }))) return;
    await api.post(`/org/applications/${el.dataset.id}/decision`, { body: { action: el.dataset.action } });
    toast(shortlist ? 'Shortlisted. A chat is open.' : 'Application rejected', shortlist ? 'check' : 'out');
    await reload();
  },
  'app.hire': (el) => openModal({
    title: `Hire ${el.dataset.name}`,
    wide: true,
    body: html`<form id="hire-form" data-submit="app.hire.send" data-id="${el.dataset.id}" data-role="${el.dataset.role}" novalidate>
      ${field('hr-title', 'Project title', { name: 'title', val: el.dataset.title, minlen: 3, maxlength: 160 })}
      ${field('hr-ms', 'Milestones', { name: 'milestones', rows: 6, max: 3000, ph: 'One per line: title | amount in rupees\nDesign mockups | 10000\nBuild and handover | 15000', help: `Freelancer quoted ${inr(el.dataset.quote)}. List 1 to 20 milestones.` })}</form>
      ${banner('VConnect records milestones and progress. It does not hold or move money. Pay the freelancer directly as agreed.', '', 'info')}`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="hire-form" class="btn">Start project</button>`,
  }),
  'app.hire.send': (form) => submitForm(form, async ({ title, milestones }) => {
    const list = parseMilestones(milestones);
    if (!list.length || list.length > 20) throw new Error('Add between 1 and 20 milestones.');
    if (list.some((m) => !m.title || m.title.length < 2 || !Number.isFinite(m.amount_paise) || m.amount_paise < 0)) throw new Error('Each line needs a title, then | and an amount in rupees.');
    const { project } = await api.post(`/org/applications/${form.dataset.id}/hire`, { body: { title, milestones: list } });
    closeModal(false);
    toast('Project started', 'check');
    await navigate(`/${form.dataset.role}/projects/${project.id}`);
  }, { onError: (error) => toast(error.message, 'alert') }),
});

/* ---------- find talent / pipeline overviews ---------- */

function talentIndexRoute(role, key) {
  return {
    path: `/${role}/${key}`,
    roles: [role],
    title: role === 'company' ? 'Find talent' : 'Find people',
    load: (ctx) => api.get('/org/requirements', { signal: ctx.signal }),
    view(d) {
      const open = d.requirements.filter((r) => r.status !== 'in_review');
      return html`${pageHead(role === 'company' ? 'Find talent' : 'Find people', 'Candidates are matched to each requirement by Vignova. Pick a requirement to see who fits.')}
        ${open.length
          ? table(['Requirement', 'Type', 'Status', 'Matched', ''], open.map((r) => [r.title, r.type, statusTag(r.status), r.matched, html`<a class="btn sm" href="/${role}/requirements/${r.id}?tab=talent">View candidates</a>`]))
          : empty('target', 'Post a requirement first', 'Candidates are matched to requirements Vignova has approved.', html`<a class="btn" href="/${role}/requirements/new">Post requirement</a>`)}`;
    },
  };
}

function pipelineIndexRoute(role, key) {
  return {
    path: `/${role}/${key}`,
    roles: [role],
    title: 'Hiring pipeline',
    async load(ctx) {
      const { requirements } = await api.get('/org/requirements', { signal: ctx.signal });
      const lists = await Promise.all(requirements.map(async (r) => {
        const { pipeline } = await api.get(`/org/requirements/${r.id}/pipeline`, { signal: ctx.signal });
        return pipeline.map((e) => ({ ...e, requirement_title: r.title }));
      }));
      return { entries: lists.flat() };
    },
    view(d) {
      const counts = STAGES.map((s) => [s, d.entries.filter((e) => e.stage === s).length]);
      return html`${pageHead('Hiring pipeline', 'Everyone you have shortlisted, across all requirements.')}
        <div class="row mb">${counts.map(([s, n]) => html`<span class="tag n">${s} ${n}</span>`)}</div>
        ${pipelineList(d.entries, role, true)}`;
    },
  };
}

export const routes = [
  ...forRoles((role) => newRoute(role)),
  ...forRoles((role) => listRoute(role)),
  ...forRoles((role) => detailRoute(role)),
  talentIndexRoute('company', 'talent'),
  talentIndexRoute('startup', 'people'),
  pipelineIndexRoute('company', 'shortlisted'),
  pipelineIndexRoute('startup', 'shortlisted'),
];
